import { useMemo } from 'react';

import { type AssetSearchFilters } from '@air/api/types';

import { useFilterParams } from '~/hooks/filters/useFilterParams';
import { useIsFlattenedView } from '~/hooks/search/useIsFlattenedView';
import { useSearchParams } from '~/hooks/search/useSearchParams';
import { useFiltersContext } from '~/providers/FiltersProvider';

export type UseClipsFiltersParams = {
  includeDescendants?: boolean;
};

export const useClipsFilters = ({ includeDescendants }: UseClipsFiltersParams = {}) => {
  const { isFlattenedView } = useIsFlattenedView();
  const { hasSearchParams } = useSearchParams();
  const { filters: userDefinedFilters, immutableFilters } = useFiltersContext();

  const {
    uploadedDateParam,
    updatedDateParam,
    takenDateParam,
    sourcesParam,
    tagsParam,
    bookmarkedParam,
    uploadersParam,
    createdDateParam,
    customFieldsParam,
    colorsParam,
    boardParam,
    extensionsParam,
    isOnBoardsParam,
    hasVersionsParam,
    hasOpenCommentsParam,
    untaggedParam,
    libraryParam,
    onlyInLibrariesParam,
    importedKeywordsParam,
    smartTagsParam,
    copyrightsParam,
    creatorsParam,
    camerasParam,
    videoFrameRatesParam,
    locationParam,
    videoAspectRatiosParam,
    audioCodingsParam,
    audioSampleRatesParam,
    peopleParam,
    locationsParam,
    assetFileKindParam,
    orientationsParam,
    semanticSimilarToImageParam,
    semanticClipSearchParam,
  } = useFilterParams();

  const filtersParams: AssetSearchFilters = useMemo(() => {
    const filtersParams: AssetSearchFilters = {};

    if (boardParam) {
      const hasAnyFilterApplied = Object.keys(userDefinedFilters).length > 0;
      if (isFlattenedView || includeDescendants || hasSearchParams || hasAnyFilterApplied) {
        filtersParams.boardTree = { is: boardParam?.is };
      } else {
        filtersParams.board = boardParam;
      }
    }

    if (smartTagsParam) {
      filtersParams.smartTag = smartTagsParam;
    }

    if (tagsParam) {
      filtersParams.tag = tagsParam;
    }

    if (sourcesParam) {
      filtersParams.source = sourcesParam;
    }

    if (uploadersParam) {
      filtersParams.member = uploadersParam;
    }

    if (colorsParam) {
      filtersParams.color = colorsParam;
    }

    if (bookmarkedParam) {
      filtersParams.bookmarked = bookmarkedParam;
    }

    if (createdDateParam) {
      filtersParams.recorded = createdDateParam;
    }

    if (updatedDateParam) {
      filtersParams.updated = updatedDateParam;
    }

    if (uploadedDateParam) {
      filtersParams.created = uploadedDateParam;
    }

    if (takenDateParam) {
      filtersParams.dateTaken = takenDateParam;
    }

    if (customFieldsParam.length) {
      filtersParams.customFields = customFieldsParam;
    }

    if (extensionsParam) {
      filtersParams.extension = extensionsParam;
    }

    if (isOnBoardsParam) {
      filtersParams.isOnBoards = isOnBoardsParam;
    }

    if (hasOpenCommentsParam) {
      filtersParams.hasOpenComments = hasOpenCommentsParam;
    }

    if (hasVersionsParam) {
      filtersParams.hasMultipleVersions = hasVersionsParam;
    }

    if (untaggedParam) {
      filtersParams.untagged = untaggedParam;
    }

    if (libraryParam) {
      filtersParams.library = libraryParam;
    }

    if (onlyInLibrariesParam) {
      filtersParams.onlyInLibraries = onlyInLibrariesParam;
    }

    if (locationParam) {
      filtersParams.location = locationParam;
    }

    if (creatorsParam) {
      filtersParams.creator = creatorsParam;
    }

    if (importedKeywordsParam) {
      filtersParams.keyword = importedKeywordsParam;
    }

    if (copyrightsParam) {
      filtersParams.copyright = copyrightsParam;
    }

    if (camerasParam) {
      filtersParams.camera = camerasParam;
    }

    if (videoFrameRatesParam) {
      filtersParams.videoFrameRate = videoFrameRatesParam;
    }

    if (videoAspectRatiosParam) {
      filtersParams.videoAspectRatio = videoAspectRatiosParam;
    }

    if (audioCodingsParam) {
      filtersParams.audioCoding = audioCodingsParam;
    }

    if (audioSampleRatesParam) {
      filtersParams.audioSampleRate = audioSampleRatesParam;
    }

    if (peopleParam) {
      filtersParams.person = peopleParam;
    }

    if (locationsParam) {
      filtersParams.boardTree = locationsParam.boards;

      if (immutableFilters?.boardId) {
        if (filtersParams.boardTree && !filtersParams.boardTree.or?.includes(immutableFilters.boardId)) {
          filtersParams.boardTree.or?.push(immutableFilters.boardId);
        } else {
          filtersParams.boardTree = {
            or: [immutableFilters.boardId],
          };
        }
      }

      filtersParams.library = locationsParam.libraries;

      if (immutableFilters?.libraryId) {
        if (filtersParams.library && !filtersParams.library.or?.includes(immutableFilters.libraryId)) {
          filtersParams.library.or?.push(immutableFilters.libraryId);
        } else {
          filtersParams.library = {
            or: [immutableFilters.libraryId],
          };
        }
      }
    }

    if (assetFileKindParam) {
      filtersParams.assetFileKind = assetFileKindParam;
    }

    if (orientationsParam) {
      filtersParams.orientation = orientationsParam;
    }

    if (semanticSimilarToImageParam) {
      filtersParams.similarToImage = semanticSimilarToImageParam;
    }

    if (semanticClipSearchParam) {
      filtersParams.similarTo = semanticClipSearchParam;
    }

    return filtersParams;
  }, [
    boardParam,
    smartTagsParam,
    tagsParam,
    sourcesParam,
    uploadersParam,
    colorsParam,
    bookmarkedParam,
    createdDateParam,
    updatedDateParam,
    uploadedDateParam,
    takenDateParam,
    customFieldsParam,
    extensionsParam,
    isOnBoardsParam,
    hasOpenCommentsParam,
    hasVersionsParam,
    untaggedParam,
    libraryParam,
    onlyInLibrariesParam,
    locationParam,
    creatorsParam,
    importedKeywordsParam,
    copyrightsParam,
    camerasParam,
    videoFrameRatesParam,
    videoAspectRatiosParam,
    audioCodingsParam,
    audioSampleRatesParam,
    peopleParam,
    locationsParam,
    assetFileKindParam,
    userDefinedFilters,
    isFlattenedView,
    includeDescendants,
    hasSearchParams,
    immutableFilters?.boardId,
    immutableFilters?.libraryId,
    orientationsParam,
    semanticSimilarToImageParam,
    semanticClipSearchParam,
  ]);

  const requiresSemanticSearch = !!semanticSimilarToImageParam || !!semanticClipSearchParam;

  return {
    filtersParams,
    requiresSemanticSearch,
  };
};
